import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import { createTarget } from "../../../../services/program.service";

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const TargetForm = ({ programId, pos, psos, onClose }) => {
  const [outcomeType, setOutcomeType] = useState("PO");
  const [outcomeId, setOutcomeId] = useState("");
  const [targetLevel, setTargetLevel] = useState(2.5);
  const [validFromBatch, setValidFromBatch] = useState("");
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Reset outcome selection when switching type
  useEffect(() => {
    setOutcomeId("");
  }, [outcomeType]);

  const options = outcomeType === "PO" ? pos : psos;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!outcomeId) {
      setError("Please pick an outcome.");
      return;
    }
    const lvl = Number(targetLevel);
    if (isNaN(lvl) || lvl < 0 || lvl > 3) {
      setError("Target level must be a number between 0 and 3.");
      return;
    }
    setSubmitting(true);
    try {
      await createTarget(programId, {
        outcomeType,
        outcomeId,
        targetLevel: lvl,
        validFromBatch: validFromBatch ? Number(validFromBatch) : null,
        rationale: rationale.trim(),
      });
      toast.success("Target created");
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-xl"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>Custom Target</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                New Attainment Target
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                  Outcome Type
                </label>
                <select
                  value={outcomeType}
                  onChange={(e) => setOutcomeType(e.target.value)}
                  className={inputBase}
                >
                  <option value="PO">PO (Program Outcome)</option>
                  <option value="PSO">PSO (Program Specific Outcome)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                  Outcome *
                </label>
                <select
                  value={outcomeId}
                  onChange={(e) => setOutcomeId(e.target.value)}
                  required
                  className={inputBase}
                >
                  <option value="">Select…</option>
                  {options.map((o) => (
                    <option key={o._id} value={o._id}>
                      {o.code} — {o.category || (o.statement || "").slice(0, 40)}
                    </option>
                  ))}
                </select>
                {options.length === 0 && (
                  <p className="mt-1 text-xs text-red-500">
                    No {outcomeType}s defined yet.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                  Target Level (0 – 3)
                </label>
                <input
                  type="number"
                  min={0}
                  max={3}
                  step={0.1}
                  value={targetLevel}
                  onChange={(e) => setTargetLevel(e.target.value)}
                  className={inputBase}
                />
                <p className="mt-1 text-xs text-gray-400">NBA typical: 2.0 – 2.5</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                  Valid From Batch
                </label>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={validFromBatch}
                  onChange={(e) => setValidFromBatch(e.target.value)}
                  placeholder="e.g. 2024"
                  className={inputBase}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Leave empty to apply to all batches.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                Rationale
              </label>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                rows={3}
                placeholder="Why this target level? (NBA evaluators may ask.)"
                className={cn(inputBase, "resize-y")}
              />
            </div>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting ? "Saving…" : "Create target"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TargetForm;
